import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Table, Tag, Avatar, Spin, Icon } from 'antd';
import AdminDashboardLayout from '../Common/AdminDashboardLayout/AdminDashboardLayout';
import { fetchManagedHosts } from './HostDashboardActions';
import * as hostReportActions from '../../redux/hostReport/hostReportActions';
import { person_white } from '../Common/Images';
import '../../assets/scss/HostReport.scss';
import '../../assets/scss/UserManagement.scss';

const UserManagement = () => {
  const { country } = useParams();
  const dispatch = useDispatch();
  
  const managedHosts = useSelector((state) => (state && state.hostDashboard && state.hostDashboard.data && state.hostDashboard.data.managedHosts) || []);
  const isFetching = useSelector((state) => (state && state.hostDashboard && state.hostDashboard.fetching));
  const { spaceList, loading: spacesLoading } = useSelector(state => state.hostReport);

  useEffect(() => {
    dispatch(fetchManagedHosts());
    dispatch(hostReportActions.getHostSpaces());
  }, [dispatch]);

  // Flatten data for rowSpan/colSpan display
  const tableData = [];
  managedHosts.forEach(host => {
    const hostSpaces = spaceList ? spaceList.filter(s => s.owner === host.email) : [];
    if (hostSpaces.length === 0) {
      tableData.push({
        ...host,
        isHostRow: true,
        rowSpan: 1,
        spaceName: 'No spaces assigned',
        spaceStatus: null,
        colSpan: 2,
        key: `host-${host.id}`
      });
    } else {
      hostSpaces.forEach((space, index) => {
        tableData.push({
          ...host,
          isHostRow: index === 0,
          rowSpan: index === 0 ? hostSpaces.length : 0,
          spaceName: space.spaceName,
          spaceStatus: space.status,
          colSpan: 1,
          key: `host-${host.id}-space-${space.id}`
        });
      });
    }
  });

  const columns = [
    {
      title: 'Host Name',
      key: 'host',
      render: (text, record) => {
        return {
          children: (
            <div className="host-info-cell">
              <Avatar 
                src={record.image || person_white} 
                icon="user" 
                className="host-avatar"
              />
              <span className="host-name">{record.firstName} {record.lastName}</span>
            </div>
          ),
          props: {
            rowSpan: record.rowSpan,
          },
        };
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => {
        return {
          children: text,
          props: {
            rowSpan: record.rowSpan,
          },
        };
      }
    },
    {
      title: 'User Status',
      dataIndex: 'status',
      key: 'status',
      width: '150px',
      render: (status, record) => {
        const isActive = status === 1 || status === true || status === "1" || status === "ACTIVE";
        return {
          children: (
            <Tag color={isActive ? 'green' : 'red'} className="status-tag">
              {isActive ? 'ACTIVE' : 'INACTIVE'}
            </Tag>
          ),
          props: {
            rowSpan: record.rowSpan,
          },
        };
      }
    },
    {
      title: 'Managed Space',
      dataIndex: 'spaceName',
      key: 'spaceName',
      render: (text, record) => {
        const isNoSpace = text === 'No spaces assigned';
        return {
          children: <span className={isNoSpace ? "no-spaces-text" : ""}>{text}</span>,
          props: {
            colSpan: record.colSpan,
          },
        };
      }
    },
    {
      title: 'Space Status',
      dataIndex: 'spaceStatus',
      key: 'spaceStatus',
      render: (status, record) => {
        if (record.colSpan === 2) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return (
          <Tag color={status === 'ACTIVE' ? 'blue' : 'orange'} className="space-tag">
            {status || 'N/A'}
          </Tag>
        );
      }
    }
  ];

  return (
    <AdminDashboardLayout>
      <div className="report-container-wrapper host-report-page host-management-page">
          <div className="dashboard-header">
                <h1>
                  Host Management
                </h1>
                <div className="dashboard-controls">
                    <p className="sub-title">Managed Host Accounts & Spaces</p>
                </div>
          </div>

          <div className="dashboard-content">
            <div className="dashboard-section">
              <Spin spinning={isFetching || spacesLoading} indicator={<Icon type="loading" className="loading-icon" spin />}>
                <Table 
                  columns={columns} 
                  dataSource={tableData} 
                  pagination={{ 
                    pageSize: 10,
                    hideOnSinglePage: true
                  }}
                  locale={{ emptyText: 'No managed host accounts found.' }}
                  className="host-management-table"
                />
              </Spin>
            </div>
          </div>
      </div>
    </AdminDashboardLayout>
  );
};

export default UserManagement;
